/**
 * Type definitions for the Position Opener / Entry feature.
 *
 * Mirrors the unwinding types module but inverted for BUY-side semantics:
 * - Unwinding "contractsToSell" → entry "contractsToBuy"
 * - Unwinding "contractsSoldKalshi/Polymarket" → entry "contractsBoughtKalshi/Polymarket"
 *
 * The entry engine evaluates whether the SUM of both legs' ASK prices is
 * below a user-configured threshold AND below the absolute hard cap of
 * $0.995 (anything >= $1 is unprofitable by construction — buying both
 * sides of a binary market for >= $1 cannot yield arbitrage).
 */

export interface TopOfBook {
  price: number;
  size: number;
}

/**
 * Hard upper bound for the sum of ASKs at which an entry order may be
 * placed. Enforced in three places on the frontend (input validator,
 * confirmation banner, Activate button disabled state) and again on the
 * backend. There is intentionally no admin override.
 */
export const ENTRY_HARD_CAP_GROSS = 0.995;

/**
 * Default target TNA percent suggested for new entry rules. Operators
 * adjust per pair — this is just the seeded value when creating a rule.
 */
export const DEFAULT_TARGET_TNA_PERCENT = 40;

export interface EntrySimulationBreakdown {
  rawTakerAsk: number;
  netTakerAsk: number;
  takerFeeRate: number;
  takerFeeAmount: number;
  takerPlatform: string;
  targetTna: number;
  daysToExpiry: number;
  effectiveDays: number;
  maxBuyPrice: number;
  makerBidTopPrice: number;
  makerBidTopSize: number;
  joinedExistingLevel: boolean;
  requestedSize: number;
  takerLiquidityCap: number;
  positionCap: number;
  effectiveSize: number;
  makerPrice: number;
  makerFeeRate: number;
  makerFeeAmount: number;
  netMakerPrice: number;
  achievedTna: number;
  unplaceableNoMillis?: boolean;
}

export interface EntrySimulationResult {
  makerPrice: number;
  takerPrice: number;
  tna: number;
  effectiveSize: number;
  breakdown?: EntrySimulationBreakdown;
}

export type EntryRuleStatus = 'active' | 'paused' | 'completed' | 'cancelled';
export type EntryRuleType = 'tna' | 'sum_of_sides';

export interface EnrichedEntryStatus {
  ruleId: string;
  pairId?: string;
  state: string;
  makerPlatform: 'kalshi' | 'polymarket';
  makerOrderId: string | null;
  makerPrice: number | null;
  makerFilledQty: number;
  makerFilledPrice: number;
  counterOrderId: string | null;
  counterFilledQty: number;
  lastStateChange: string;
  errorMessage: string | null;
  pairName: string;
  expiryDate: string;
  daysToExpiry: number;
  polyBid: TopOfBook | null;
  polyAsk: TopOfBook | null;
  kalshiBid: TopOfBook | null;
  kalshiAsk: TopOfBook | null;
  /** Sum of both legs' ASK prices — the entry-side analogue of the
   *  unwinder's TT. When this drops below the rule's threshold AND below
   *  ENTRY_HARD_CAP_GROSS, the engine fires. */
  currentSumOfAsks: number | null;
  simulation: {
    polyAsMaker: EntrySimulationResult | null;
    kalshiAsMaker: EntrySimulationResult | null;
  };
  kalshiConfig: {
    side: 'yes' | 'no';
    ticker: string;
    contractsToBuy: number;
    targetTnaPercent: number;
  } | null;
  polymarketConfig: {
    side: 'yes' | 'no';
    tokenId: string;
    conditionId: string;
    contractsToBuy: number;
    targetTnaPercent: number;
  } | null;
  maxVolumeUsd: number;
  volumePeriod: 'hour' | 'day';
  volumeUsed: number;
  volumeRemaining: number;
  kalshiMarketUrl: string | null;
  polymarketMarketUrl: string | null;
  simTna?: number;
  simSize?: number;
  simMaxVolume?: number;
  appliedSimTna?: number;
  appliedSimSize?: number;
  appliedSimMaxVolume?: number;
  hasPendingSim?: boolean;

  // ── Two-path market data (BUY side) ─────────────────────────────────────
  // A prediction-market arb has two entry paths; the enricher emits
  // both sides' quotes for both platforms so the row can show both
  // sums while market data + sim cells track the lower-sum / committed
  // path. See position-opener's `EnrichedRuleState` for the contract.
  polyYesBid?: TopOfBook | null;
  polyYesAsk?: TopOfBook | null;
  polyNoBid?: TopOfBook | null;
  polyNoAsk?: TopOfBook | null;
  kalshiYesBid?: TopOfBook | null;
  kalshiYesAsk?: TopOfBook | null;
  kalshiNoBid?: TopOfBook | null;
  kalshiNoAsk?: TopOfBook | null;
  /** Gross ask sum for Path A (poly_yes_ask + kalshi_no_ask). */
  sumPathA?: number | null;
  /** Gross ask sum for Path B (poly_no_ask + kalshi_yes_ask). */
  sumPathB?: number | null;
  /** Which path currently has the lower gross sum. */
  lowerPath?: 'a' | 'b' | null;
  /**
   * Non-null when contracts have been bought on this rule. Locks the
   * UI to the committed path: only its 2 toggles are editable and
   * only its sum/market-data render.
   */
  committedPath?: 'a' | 'b' | null;
  /** Per-leg maker sim cells (orange = taker net, blue = maker rest). */
  simPolyYesMaker?: { taker: number | null; maker: number | null } | null;
  simPolyNoMaker?: { taker: number | null; maker: number | null } | null;
  simKalshiYesMaker?: { taker: number | null; maker: number | null } | null;
  simKalshiNoMaker?: { taker: number | null; maker: number | null } | null;
}

// ── Phase 6 — split-shape envelope ──────────────────────────────────────
// The new `OpeningPageState` decouples pair-scoped market data from
// rule-scoped engine state. The hook listens to `opening_state` /
// `opening_state_update` and projects these envelopes back into the
// legacy `EnrichedEntryStatus` shape so existing row components keep
// working. Phase 7 will rewrite the row components to consume the
// split shape directly and the projection will be removed.
export interface MarketState {
  pairId: string;
  pairName: string;
  expiryDate: string;
  daysToExpiry: number;
  polyYesBid: TopOfBook | null;
  polyYesAsk: TopOfBook | null;
  polyNoBid: TopOfBook | null;
  polyNoAsk: TopOfBook | null;
  kalshiYesBid: TopOfBook | null;
  kalshiYesAsk: TopOfBook | null;
  kalshiNoBid: TopOfBook | null;
  kalshiNoAsk: TopOfBook | null;
  kalshiMarketUrl: string | null;
  polymarketMarketUrl: string | null;
}

export interface OpenerRuleState {
  ruleId: string;
  state: string;
  makerPlatform: 'kalshi' | 'polymarket';
  makerOrderId: string | null;
  makerPrice: number | null;
  makerFilledQty: number;
  makerFilledPrice: number;
  counterOrderId: string | null;
  counterFilledQty: number;
  lastStateChange: string;
  errorMessage: string | null;
  kalshiConfig: EnrichedEntryStatus['kalshiConfig'];
  polymarketConfig: EnrichedEntryStatus['polymarketConfig'];
  simulation: EnrichedEntryStatus['simulation'];
  maxVolumeUsd: number;
  volumePeriod: 'hour' | 'day';
  volumeUsed: number;
  volumeRemaining: number;
  simTna?: number;
  simSize?: number;
  simMaxVolume?: number;
  appliedSimTna?: number;
  appliedSimSize?: number;
  appliedSimMaxVolume?: number;
  hasPendingSim?: boolean;
  kalshiOrderState: unknown;
  polyOrderState: unknown;
  sumPathA: number | null;
  sumPathB: number | null;
  lowerPath: 'a' | 'b' | null;
  committedPath: 'a' | 'b' | null;
  simPolyYesMaker: { taker: number | null; maker: number | null } | null;
  simPolyNoMaker: { taker: number | null; maker: number | null } | null;
  simKalshiYesMaker: { taker: number | null; maker: number | null } | null;
  simKalshiNoMaker: { taker: number | null; maker: number | null } | null;
}

export interface OpeningPageState {
  pairId: string;
  market: MarketState;
  rule: OpenerRuleState | null;
  // Always-computed (backend) path + sim payload — populated for
  // every pair regardless of whether a rule is active. The threshold
  // used for sim cells is `rule.thresholdSum` when the rule exists,
  // else `OPENER_PREVIEW_THRESHOLD` (0.95) on the backend.
  sumPathA?: number | null;
  sumPathB?: number | null;
  lowerPath?: 'a' | 'b' | null;
  simPolyYesMaker?: { taker: number | null; maker: number | null } | null;
  simPolyNoMaker?: { taker: number | null; maker: number | null } | null;
  simKalshiYesMaker?: { taker: number | null; maker: number | null } | null;
  simKalshiNoMaker?: { taker: number | null; maker: number | null } | null;
}

export type MakerFlag =
  | 'polyYesMaker'
  | 'polyNoMaker'
  | 'kalshiYesMaker'
  | 'kalshiNoMaker';

export interface EntryRule {
  _id: string;
  pairId: string;
  pairName: string;
  expiryDate: string;
  customExpiryDate?: string | null;
  category?: string;
  ruleType?: EntryRuleType;
  /** Pair-level threshold for sum_of_sides rules; the engine fires when
   *  currentSumOfAsks <= thresholdSum AND < ENTRY_HARD_CAP_GROSS. */
  thresholdSum?: number | null;
  kalshiConfig: {
    side: 'yes' | 'no';
    ticker: string;
    contractsToBuy: number;
    targetTnaPercent?: number | null;
  } | null;
  polymarketConfig: {
    side: 'yes' | 'no';
    tokenId: string;
    conditionId: string;
    contractsToBuy: number;
    targetTnaPercent?: number | null;
  } | null;
  kalshiEnabled: boolean;
  polymarketEnabled: boolean;
  maxVolumeUsd: number;
  volumePeriod: 'hour' | 'day';
  status: EntryRuleStatus;
  contractsBoughtKalshi: number;
  contractsBoughtPolymarket: number;
  totalVolumeExecutedUsd: number;
  activeKalshiOrderId: string | null;
  activePolymarketOrderId: string | null;
  lastExecutionAt: string | null;
  activatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // 4 per-(platform, side) maker toggles. See backend EntryRule schema
  // for the path-exclusivity + position-bought-lock contract.
  polyYesMaker?: boolean;
  polyNoMaker?: boolean;
  kalshiYesMaker?: boolean;
  kalshiNoMaker?: boolean;
}

export interface EntryExecution {
  _id: string;
  ruleId: string;
  pairId: string;
  pairName: string;
  direction: 'kalshi_maker' | 'polymarket_maker';
  makerLeg: {
    orderId: string;
    platform: string;
    side: string;
    price: number;
    contracts: number;
    fee: number;
    status: string;
  };
  takerLeg: {
    orderId: string;
    platform: string;
    side: string;
    price: number;
    contracts: number;
    fee: number;
    orderType: string;
    status: string;
  };
  totalVolumeUsd: number;
  effectiveTnaPercent: number;
  capitalDeployed: number;
  overallStatus:
    | 'complete'
    | 'partial'
    | 'maker_only'
    | 'failed'
    | 'pending_counter';
  errorMessage?: string;
  createdAt: string;
}

export interface CreateEntryRuleDto {
  pairId: string;
  pairName: string;
  expiryDate: string;
  customExpiryDate?: string | null;
  ruleType?: EntryRuleType;
  thresholdSum?: number;
  kalshiConfig?: {
    side: 'yes' | 'no';
    ticker: string;
    contractsToBuy: number;
    targetTnaPercent?: number;
  };
  polymarketConfig?: {
    side: 'yes' | 'no';
    tokenId: string;
    conditionId: string;
    contractsToBuy: number;
    targetTnaPercent?: number;
  };
  maxVolumeUsd: number;
  volumePeriod: 'hour' | 'day';
  kalshiEnabled?: boolean;
  polymarketEnabled?: boolean;
  polyYesMaker?: boolean;
  polyNoMaker?: boolean;
  kalshiYesMaker?: boolean;
  kalshiNoMaker?: boolean;
}

export interface UpdateEntryRuleDto {
  thresholdSum?: number;
  kalshiConfig?: {
    side?: 'yes' | 'no';
    ticker: string;
    contractsToBuy: number;
    targetTnaPercent?: number;
  };
  polymarketConfig?: {
    side?: 'yes' | 'no';
    tokenId: string;
    conditionId: string;
    contractsToBuy: number;
    targetTnaPercent?: number;
  };
  maxVolumeUsd?: number;
  volumePeriod?: 'hour' | 'day';
  customExpiryDate?: string | null;
  polyYesMaker?: boolean;
  polyNoMaker?: boolean;
  kalshiYesMaker?: boolean;
  kalshiNoMaker?: boolean;
}

/**
 * A pair surfaced by the pair-manager as `manual-confirmed` and `active`
 * — these are the only pairs eligible for entry rule creation. Schema is
 * kept loose because the pair-manager's `manual-confirmed` endpoint is
 * the canonical source: extra fields just pass through.
 */
export interface ManualConfirmedPair {
  _id: string;
  pairName: string;
  polymarketTitle?: string;
  kalshiTitle?: string;
  expiryDate?: string | null;
  category?: string | null;
  status?: string;
  archived?: boolean;
  // Polymarket leg identifiers (mirrored to entry rule polymarketConfig)
  polymarket?: {
    tokenId?: string;
    conditionId?: string;
    side?: 'yes' | 'no';
    marketUrl?: string;
  } | null;
  // Kalshi leg identifiers (mirrored to entry rule kalshiConfig)
  kalshi?: {
    ticker?: string;
    side?: 'yes' | 'no';
    marketUrl?: string;
  } | null;
}

export interface NormalizedOrder {
  id: string;
  platform: 'kalshi' | 'polymarket';
  marketId: string;
  marketTitle?: string;
  marketUrl?: string;
  side: 'yes' | 'no';
  action: 'buy' | 'sell';
  price: number;
  quantity: number;
  filledQuantity: number;
  remainingQuantity: number;
  status: string;
  isEntry: boolean;
  ruleId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrdersSummary {
  kalshi: { count: number; totalVolume: number };
  polymarket: { count: number; totalVolume: number };
}

export interface EntryOrdersResponse {
  orders: NormalizedOrder[];
  summary: OrdersSummary;
}
